import React, { useState } from 'react';
import { AkunKeuangan } from '../../types/index.js';
import { Plus, Search, ChevronRight, Edit2, Trash2 } from 'lucide-react';
import { useDataTable } from '../../hooks/useDataTable.js';
import { Pagination } from '../ui/Pagination.js';
import { useDialog } from '../ui/DialogProvider.js';

interface AkunDetailViewProps {
  akunList: AkunKeuangan[];
  onAddAkun: (data: Partial<AkunKeuangan>) => Promise<void>;
  onUpdateAkun: (id: string, data: Partial<AkunKeuangan>) => Promise<void>;
  onDeleteAkun: (id: string) => Promise<void>;
}

export const AkunDetailView: React.FC<AkunDetailViewProps> = ({ akunList, onAddAkun, onUpdateAkun, onDeleteAkun }) => {
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const { showConfirm } = useDialog();
  const [id, setId] = useState('');
  const [namaAkun, setNamaAkun] = useState('');
  const [level, setLevel] = useState<number>(1);
  const [tipeAkun, setTipeAkun] = useState<'ASET' | 'KEWAJIBAN' | 'EKUITAS' | 'PENDAPATAN' | 'BIAYA'>('ASET');
  const [parentId, setParentId] = useState('');
  const [saldoNormal, setSaldoNormal] = useState<'DEBIT' | 'KREDIT'>('DEBIT');
  const [isTransaksional, setIsTransaksional] = useState(false);

  const {
    searchQuery: search,
    setSearchQuery: setSearch,
    currentPage,
    setCurrentPage,
    rowsPerPage,
    setRowsPerPage,
    totalPages,
    totalRows,
    paginatedData: filteredAkun,
  } = useDataTable<AkunKeuangan>({
    data: akunList,
    searchFields: ['id', 'namaAkun', 'tipeAkun'],
    initialSortBy: 'id',
  });

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload = { id, namaAkun, level, tipeAkun, parentId: parentId || null, saldoNormal, isTransaksional };
    if (isEditing) {
      await onUpdateAkun(id, payload);
    } else {
      await onAddAkun(payload);
    }
    setShowModal(false);
  };

  const handleEditClick = (a: AkunKeuangan) => {
    setIsEditing(true);
    setId(a.id);
    setNamaAkun(a.namaAkun);
    setLevel(a.level);
    setTipeAkun(a.tipeAkun as any);
    setParentId(a.parentId || '');
    setSaldoNormal(a.saldoNormal as any);
    setIsTransaksional(a.isTransaksional);
    setShowModal(true);
  };

  const handleAddClick = () => {
    setIsEditing(false);
    setId('');
    setNamaAkun('');
    setLevel(1);
    setTipeAkun('ASET');
    setParentId('');
    setSaldoNormal('DEBIT');
    setIsTransaksional(false);
    setShowModal(true);
  };

  const getIndent = (level: number) => {
    return (level - 1) * 20; // 20px per level
  };

  return (
    <div className="space-y-4 animate-in fade-in duration-200">
      {/* Top Action Bar */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-base font-bold text-slate-900">Chart of Accounts (COA)</h2>
          <p className="text-xs text-slate-500">
            Struktur buku kas bank, piutang, pendapatan, dan beban SDMT Ponorogo
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Cari kode / nama akun..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 pr-3 py-1.5 text-xs bg-slate-50 border border-slate-300 rounded-md focus:ring-1 focus:ring-sky-500 w-48"
            />
          </div>

          <button
            onClick={handleAddClick}
            className="flex items-center gap-1.5 bg-sky-600 hover:bg-sky-500 text-white text-xs font-semibold px-3 py-1.5 rounded-md transition shadow-xs"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>Tambah Akun</span>
          </button>
        </div>
      </div>

      {/* Main Akun Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden flex flex-col min-h-[400px]">
        <div className="overflow-x-auto flex-1">
          <table className="w-full text-xs text-left">
            <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 font-bold uppercase sticky top-0 z-10">
              <tr>
                <th className="py-2.5 px-4 w-12 text-center">No</th>
                <th className="py-2.5 px-4 w-28 text-center">Aksi</th>
                <th className="py-2.5 px-4">Kode Akun</th>
                <th className="py-2.5 px-4">Nama Akun</th>
                <th className="py-2.5 px-4 text-center">Level</th>
                <th className="py-2.5 px-4">Tipe</th>
                <th className="py-2.5 px-4 text-center">Jurnal</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredAkun.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-8 text-slate-400">
                    Tidak ada data akun yang cocok
                  </td>
                </tr>
              ) : (
                filteredAkun.map((a, idx) => (
                  <tr key={a.id} className="hover:bg-sky-50/50 transition">
                    <td className="py-2.5 px-4 text-center text-slate-400 font-mono">
                      {(currentPage - 1) * rowsPerPage + idx + 1}
                    </td>
                    <td className="py-2.5 px-4 text-center">
                      <div className="flex justify-center gap-1">
                        <button
                          onClick={() => handleEditClick(a)}
                          className="px-1.5 py-0.5 text-[10px] font-medium bg-emerald-50 text-emerald-700 hover:bg-emerald-100 rounded border border-emerald-300 flex items-center gap-0.5"
                        >
                          <Edit2 className="w-2.5 h-2.5" />
                          <span>Edit</span>
                        </button>
                        <button
                          onClick={async () => {
                            await showConfirm(`Hapus akun ${a.namaAkun}?`, { type: 'danger', title: 'Hapus Akun' });
                            await onDeleteAkun(a.id);
                          }}
                          className="px-1.5 py-0.5 text-[10px] font-medium bg-rose-50 text-rose-700 hover:bg-rose-100 rounded border border-rose-300 flex items-center gap-0.5"
                        >
                          <Trash2 className="w-2.5 h-2.5" />
                          <span>Del</span>
                        </button>
                      </div>
                    </td>
                    <td className="py-2.5 px-4 font-mono text-slate-700 font-medium whitespace-nowrap">
                      {a.id}
                    </td>
                    <td className="py-2.5 px-4 font-medium text-slate-800" style={{ paddingLeft: `${16 + getIndent(a.level)}px` }}>
                      <div className="flex items-center gap-1.5">
                        {a.level < 4 && <ChevronRight className="w-3 h-3 text-slate-400" />}
                        {a.level === 4 && <div className="w-3 h-3 border-l-2 border-b-2 border-slate-300 rounded-bl" />}
                        {a.namaAkun}
                      </div>
                    </td>
                    <td className="py-2.5 px-4 text-center">
                      <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-[10px] font-bold">
                        Lvl {a.level}
                      </span>
                    </td>
                    <td className="py-2.5 px-4">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                        a.tipeAkun === 'ASET' ? 'bg-sky-100 text-sky-700' :
                        a.tipeAkun === 'PENDAPATAN' ? 'bg-emerald-100 text-emerald-700' :
                        a.tipeAkun === 'BIAYA' ? 'bg-rose-100 text-rose-700' :
                        a.tipeAkun === 'KEWAJIBAN' ? 'bg-amber-100 text-amber-700' :
                        'bg-purple-100 text-purple-700'
                      }`}>
                        {a.tipeAkun}
                      </span>
                    </td>
                    <td className="py-2.5 px-4 text-center">
                      {a.isTransaksional ? (
                        <span className="w-4 h-4 rounded-full bg-emerald-500 mx-auto block" title="Bisa digunakan untuk jurnal/transaksi" />
                      ) : (
                        <span className="w-4 h-4 rounded-full bg-slate-200 mx-auto block" title="Hanya induk/akumulasi" />
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalRows={totalRows}
          rowsPerPage={rowsPerPage}
          onPageChange={setCurrentPage}
          onRowsPerPageChange={setRowsPerPage}
        />
      </div>

      {/* Modal Tambah Akun Baru */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 border border-slate-200 animate-in fade-in">
            <h3 className="text-sm font-bold text-slate-900 border-b pb-3 mb-4">
              {isEditing ? 'Edit Akun Keuangan' : 'Tambah Akun Baru'}
            </h3>
            <form onSubmit={handleSave} className="space-y-4">
              <div>
                <label className="block text-[11px] font-semibold text-slate-700 mb-1">Kode Akun</label>
                <input
                  type="text"
                  required
                  disabled={isEditing}
                  value={id}
                  onChange={e => setId(e.target.value)}
                  placeholder="Contoh: 1-110003"
                  className="w-full text-xs px-3 py-2 border border-slate-300 rounded-md focus:ring-1 focus:ring-sky-500 outline-none"
                />
              </div>
              
              <div>
                <label className="block text-[11px] font-semibold text-slate-700 mb-1">Nama Akun</label>
                <input
                  type="text"
                  required
                  value={namaAkun}
                  onChange={e => setNamaAkun(e.target.value)}
                  placeholder="Contoh: Bank BSI"
                  className="w-full text-xs px-3 py-2 border border-slate-300 rounded-md focus:ring-1 focus:ring-sky-500 outline-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-semibold text-slate-700 mb-1">Level Hierarki</label>
                  <select
                    value={level}
                    onChange={e => setLevel(Number(e.target.value))}
                    className="w-full text-xs px-3 py-2 border border-slate-300 rounded-md focus:ring-1 focus:ring-sky-500 outline-none"
                  >
                    <option value={1}>Level 1 (Induk)</option>
                    <option value={2}>Level 2 (Detail)</option>
                    <option value={3}>Level 3 (Sub Detail)</option>
                    <option value={4}>Level 4 (Rincian/Buku)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-slate-700 mb-1">Tipe Akun</label>
                  <select
                    value={tipeAkun}
                    onChange={e => setTipeAkun(e.target.value as any)}
                    className="w-full text-xs px-3 py-2 border border-slate-300 rounded-md focus:ring-1 focus:ring-sky-500 outline-none"
                  >
                    <option value="ASET">Aset</option>
                    <option value="KEWAJIBAN">Kewajiban</option>
                    <option value="EKUITAS">Ekuitas / Modal</option>
                    <option value="PENDAPATAN">Pendapatan</option>
                    <option value="BIAYA">Biaya</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-semibold text-slate-700 mb-1">Kode Induk (Parent)</label>
                <select
                  value={parentId}
                  onChange={e => setParentId(e.target.value)}
                  className="w-full text-xs px-3 py-2 border border-slate-300 rounded-md focus:ring-1 focus:ring-sky-500 outline-none"
                >
                  <option value="">-- Paling Atas (Tidak Punya Induk) --</option>
                  {akunList.filter(a => a.level < 4).map(a => (
                    <option key={a.id} value={a.id}>{a.id} - {a.namaAkun}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-2 mt-4 pt-4 border-t border-slate-100">
                <input 
                  type="checkbox" 
                  id="isTrx" 
                  checked={isTransaksional} 
                  onChange={e => setIsTransaksional(e.target.checked)} 
                  className="rounded text-sky-600 focus:ring-sky-500"
                />
                <label htmlFor="isTrx" className="text-xs text-slate-700 cursor-pointer">
                  Bisa dipakai untuk transaksi (hanya Rincian)
                </label>
              </div>

              <div className="flex items-center justify-end gap-2 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-100 rounded-md transition"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-xs font-semibold bg-sky-600 hover:bg-sky-500 text-white rounded-md transition shadow-xs"
                >
                  Simpan Akun
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
